import { useEffect } from 'react';
import { PaneNode, TabState } from '../../../../types';
import { getAllTabs } from '../../../utils/layoutUtils';
import logger from '../../../utils/logger';

interface UseGlobalFunctionsProps {
  layout: {
    rootPane: PaneNode | null;
    activeContainerId: string;
  };
  activateTab: (containerId: string, tabId: string) => void;
}

/**
 * グローバル関数を管理するカスタムフック
 * - window.__getAllTabs: すべてのタブを取得
 * - window.__activateTab: 指定されたタブをアクティブ化
 * 
 * これらの関数は主にテストやデバッグ目的で使用される
 */
export function useGlobalFunctions({
  layout,
  activateTab
}: UseGlobalFunctionsProps) {
  
  // グローバル関数の登録
  useEffect(() => {
    // すべてのタブを取得する関数
    const getAllTabsGlobal = (): TabState[] => {
      if (!layout.rootPane) return [];
      const allTabs = getAllTabs(layout.rootPane);
      return allTabs;
    };
    
    // 指定されたタブをアクティブ化する関数
    const activateTabGlobal = (tabId: string) => {
      
      if (!layout.rootPane) {
        logger.warn('[useGlobalFunctions] No root pane available');
        return;
      }
      
      // タブを含むコンテナを探す
      const findContainerWithTab = (node: PaneNode, targetTabId: string): string | null => {
        if (node.type === 'container') {
          if (node.tabs.some(tab => tab.id === targetTabId)) {
            return node.id;
          }
        } else if (node.type === 'split') {
          const children = [node.leftChild, node.rightChild].filter(Boolean);
          for (const child of children) {
            const containerId = findContainerWithTab(child, targetTabId);
            if (containerId) return containerId;
          }
        }
        return null;
      };
      
      const containerId = findContainerWithTab(layout.rootPane, tabId);
      if (containerId) {
        activateTab(containerId, tabId);
      } else {
        logger.warn('[useGlobalFunctions] Tab not found:', tabId);
      }
    };
    
    // windowオブジェクトに関数を追加
    // TypeScriptの型エラーを回避するため、型アサーションを使用
    (window as typeof window & {
      __getAllTabs?: () => TabState[];
      __activateTab?: (tabId: string) => void;
    }).__getAllTabs = getAllTabsGlobal;
    
    (window as typeof window & {
      __getAllTabs?: () => TabState[];
      __activateTab?: (tabId: string) => void;
    }).__activateTab = activateTabGlobal;
    
    
    // クリーンアップ
    return () => {
      const extWindow = window as typeof window & {
        __getAllTabs?: () => TabState[];
        __activateTab?: (tabId: string) => void;
      };
      delete extWindow.__getAllTabs;
      delete extWindow.__activateTab;
    };
  }, [layout, activateTab]);
}